import { useState, useEffect, useRef } from "react";
import { FilterIcon } from "lucide-react";

const useDropdownPosition = (open) => {
  const dropRef = useRef(null);
  useEffect(() => {
    if (open && dropRef.current) {
      const rect = dropRef.current.getBoundingClientRect();
      const overflows = rect.right > window.innerWidth - 10;
      dropRef.current.style.left = overflows ? "auto" : "0";
      dropRef.current.style.right = overflows ? "0" : "auto";
    }
  }, [open]);
  return dropRef;
};

const ScoreRangeFilterDropdown = ({ currentMin, currentMax, onApply }) => {
  const [open, setOpen] = useState(false);
  const [min, setMin] = useState(currentMin ?? "");
  const [max, setMax] = useState(currentMax ?? "");
  const [error, setError] = useState("");
  const ref = useRef(null);
  const dropRef = useDropdownPosition(open);
  const isActive = currentMin !== "" || currentMax !== "";

  useEffect(() => {
    setMin(currentMin ?? "");
    setMax(currentMax ?? "");
  }, [currentMin, currentMax]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const validate = () => {
    const minVal = min === "" ? null : Number(min);
    const maxVal = max === "" ? null : Number(max);

    if (minVal !== null && (minVal < 0 || minVal > 100)) {
      setError("Min must be 0–100");
      return false;
    }
    if (maxVal !== null && (maxVal < 0 || maxVal > 100)) {
      setError("Max must be 0–100");
      return false;
    }
    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      setError("Min can't exceed Max");
      return false;
    }
    setError("");
    return true;
  };

  const handleApply = () => {
    if (!validate()) return;
    onApply(min === "" ? "" : min, max === "" ? "" : max);
    setOpen(false);
  };

  const handleClear = () => {
    setMin("");
    setMax("");
    setError("");
    onApply("", "");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleApply();
  };

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="ml-1 p-0.5 rounded transition-colors bg-transparent text-text-secondary filter-icon-btn"
      >
        <FilterIcon size={12} stroke="var(--text-secondary)" />
      </button>
      {open && (
        <div
          ref={dropRef}
          className="absolute top-full mt-2 z-[999] w-75 normal-case font-normal"
          style={{
            background: "var(--screen-background-color)",
            border: "2px solid rgba(190, 5, 250, 0.2)",
            borderRadius: "10px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            padding: "0",
          }}
        >
          <p className="text-xs font-bold text-text-main uppercase tracking-wider px-3 pt-3 pb-2">
            Overall Score
          </p>

          <div className="px-3 pb-3 space-y-3">
            {/* Min / Max inputs */}
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[0.65rem] text-text-sub font-medium uppercase tracking-wider mb-1 block">Min %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={min}
                  onChange={(e) => { setMin(e.target.value); setError(""); }}
                  onKeyDown={handleKeyDown}
                  placeholder="0"
                  className="w-full px-2.5 py-2 rounded-lg text-sm text-text-main outline-none"
                  style={{
                    background: "transparent",
                    border: "2px solid rgba(190, 5, 250, 0.2)",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--buttons-background-color)"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(190, 5, 250, 0.2)"}
                />
              </div>
              <span className="text-text-sub text-xs mt-4">–</span>
              <div className="flex-1">
                <label className="text-[0.65rem] text-text-sub font-medium uppercase tracking-wider mb-1 block">Max %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={max}
                  onChange={(e) => { setMax(e.target.value); setError(""); }}
                  onKeyDown={handleKeyDown}
                  placeholder="100"
                  className="w-full px-2.5 py-2 rounded-lg text-sm text-text-main outline-none"
                  style={{
                    background: "transparent",
                    border: "2px solid rgba(190, 5, 250, 0.2)",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--buttons-background-color)"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(190, 5, 250, 0.2)"}
                />
              </div>
            </div>

            {/* Helper text */}
            <p className="text-[0.65rem] text-text-sub leading-relaxed">
              Leave Min empty for ≤ Max. Leave Max empty for ≥ Min.
            </p>

            {/* Error */}
            {error && (
              <p className="text-[0.7rem] text-red-400 font-medium">{error}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 p-3" style={{ borderTop: "2px solid rgba(190, 5, 250, 0.2)" }}>
            <button
              onClick={handleClear}
              className="flex-1 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all"
              style={{
                border: "2px solid var(--buttons-background-color)",
                color: "var(--text-primary)",
                background: "transparent",
              }}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all"
              style={{
                background: "var(--buttons-background-color)",
                color: "white",
                border: "2px solid var(--buttons-background-color)",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

export default ScoreRangeFilterDropdown;